import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useUser } from "../contexts/UserContext";
import { decryptData } from "../utils/cloudApi";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

const PdfPreviewModal = ({ pdfUrl, invoiceData, onClose }) => {
  const { user, privateKeyInMemory } = useUser();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [decryptedPdfUrl, setDecryptedPdfUrl] = useState(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [error, setError] = useState(null);

  // Determine which mode we're in
  const needsDecryption = !!invoiceData;
  const directPdfUrl = pdfUrl;

  useEffect(() => {
    // If pdfUrl is provided directly, use it immediately
    if (directPdfUrl) {
      setDecryptedPdfUrl(directPdfUrl);
    }
    // If invoiceData is provided, decrypt it
    else if (needsDecryption) {
      const decryptInvoiceData = async () => {
        try {
          setIsDecrypting(true);
          setError(null);

          // Decrypt the invoice data to access pdfUrl
          const decrypted = await decryptData(
            invoiceData,
            user.uid,
            privateKeyInMemory
          );

          // console.log(decrypted);

          if (decrypted?.pdfUrl) {
            setDecryptedPdfUrl(decrypted.pdfUrl);
          } else {
            setError("PDF URL not found in invoice data");
          }
        } catch (err) {
          console.error("Failed to decrypt invoice data:", err);
          setError("Failed to decrypt invoice data");
        } finally {
          setIsDecrypting(false);
        }
      };

      if (user?.uid && invoiceData) {
        decryptInvoiceData();
      } else {
        setError("User authentication required");
        setIsDecrypting(false);
      }
    }
  }, [
    invoiceData,
    pdfUrl,
    user?.uid,
    privateKeyInMemory,
    needsDecryption,
    directPdfUrl,
  ]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Get the final PDF URL to display
  const finalPdfUrl = needsDecryption ? decryptedPdfUrl : directPdfUrl;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Modal backdrop */}
      <div className="absolute inset-0 bg-black opacity-50"></div>

      {/* Modal content */}
      <div className="bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 border rounded shadow-lg z-10 max-h-[95vh] h-full max-w-5xl w-full flex flex-col">
        {/* Header */}
        <div className="p-4 flex justify-between items-center border-b dark:border-gray-600">
          <h2 className="text-xl font-bold text-white">Preview</h2>
          <button onClick={onClose} className="btn-action-white">
            Close
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 flex-1 overflow-auto scrollable flex justify-center items-start">
          {isDecrypting ? (
            <p className="text-gray-300 text-center mt-8">
              Decrypting invoice data...
            </p>
          ) : error ? (
            <p className="text-red-500 text-center mt-8">{error}</p>
          ) : finalPdfUrl ? (
            <Document
              file={finalPdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <p className="text-gray-300 text-center">Loading PDF...</p>
              }
              error={
                <p className="text-red-500 text-center">Failed to load PDF.</p>
              }
            >
              <Page
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={true}
                scale={1.2}
              />
            </Document>
          ) : (
            <p className="text-gray-300 text-center mt-8">No PDF available</p>
          )}
        </div>

        {/* Footer: Pagination */}
        <div className="p-2 flex justify-between items-center border-t border-gray-400 dark:border-gray-600">
          <button
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || isDecrypting || !finalPdfUrl}
            className="btn-action-white"
          >
            Previous
          </button>
          <span className="text-sm text-white">
            Page {pageNumber} of {numPages || "..."}
          </span>
          <button
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages || isDecrypting || !finalPdfUrl}
            className="btn-action-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
